// chart-widget.component.ts
import {
  Component, Input, OnChanges, SimpleChanges,
  ViewChild, ElementRef, AfterViewInit,
  ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef  
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { Chart, registerables,ChartDataset,ChartType,ChartConfiguration } from 'chart.js';
import { ChartConfig } from '../Services/widget.models';


const stackedValueLabelsPlugin = {
  id: 'stackedValueLabels',
  afterDatasetsDraw(chart: Chart) {
    const showLabels = (chart.config.data.datasets as any)?.__showValueLabels;
    if (!showLabels || (chart.config as any).type !== 'bar') return;

    const { ctx } = chart;
    const meta0 = chart.getDatasetMeta(0);
    if (!meta0?.data?.length) return;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < meta0.data.length; i++) {
      let total = 0;
      let topBar: any = null;
       let minY = Infinity;
       let xSum = 0;
       let xCount = 0;

      chart.data.datasets.forEach((ds, dsIndex) => {
        const dsMeta = chart.getDatasetMeta(dsIndex);
        if (dsMeta.hidden) return;
        const value = Number(ds.data[i]) || 0;
        total += value;

        const bar = dsMeta.data[i] as any;
        if (!bar) return;

        if (value > 0) {
          const label = ds.label ?? '';
          ctx.font = 'bold 13px sans-serif';
          ctx.fillStyle = '#fff';
          // //ctx.fillText(`${label}: ${value}`, bar.x, (bar.y + bar.base) / 2);
          ctx.fillText(`${value}`, bar.x, (bar.y + bar.base) / 2);

          minY = Math.min(minY, bar.y);
          xSum += bar.x;
          xCount++;
        }
        topBar = bar; // last drawn = topmost segment
      });

      
    }
    ctx.restore();
  }
};

Chart.register(...registerables, stackedValueLabelsPlugin);
// Chart.register(...registerables);

const DEFAULT_COLORS = [
  '#5d87ff', '#13deb9', '#ffae1f', '#fa896b', '#49beff',
  '#7460ee', '#ff4560', '#00e396', '#feb019', '#008ffb'
];

@Component({
  selector: 'app-chart-widget',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,


template: `
  <div class="chart-shell" [ngStyle]="shellStyles">
    <div class="chart-header" *ngIf="config.title">
      <span class="chart-title">{{ config.title }}</span>
      <button
        *ngIf="hasLegend"
        class="legend-toggle"
        (click)="toggleAll()"
        [title]="allVisible ? 'Deselect all' : 'Select all'">
        <span class="toggle-icon">{{ allVisible ? '☑' : '☐' }}</span>
        {{ 'Select all' }}
      </button>
    </div>
    <div [ngClass]="{
      'chart-body': this.config.type !== 'pie' && this.config.type !== 'doughnut',
      'Pie-chart-body': this.config.type == 'pie' || this.config.type === 'doughnut'
      }">
      <canvas class="chart-canvas" #chartCanvas></canvas>
    </div>
  </div>
`,
  styles: [`
    :host { display: block; }

    .chart-shell {
      width: 100%;
      height: 100%;
      box-sizing: border-box; 
      // background: #fff;
      background: var(--dsh-primary, #fff);
      border-radius: 16px;
      box-shadow: 0 2px 12px rgba(0,0,0,.08);
      padding: 18px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: box-shadow .2s;
    }
    .chart-shell:hover { box-shadow: 0 6px 24px rgba(0,0,0,.13); }

    .chart-header {
      margin-bottom: 12px;
    }
    .chart-title {
      font-size: 15px;
      font-weight: 600;
      // color: #2a3547;
      color:var(--dsh-text, #2a3547);
    }

    .chart-body {
      flex: 1;
      position: relative;
      min-height: 0;
    }
    .Pie-chart-body{
      flex: 1;
      display:flex;
      justify-content:center;
      align-items:center;
      min-height: 0;
    }
    
    .chart-canvas {
      max-width: 100%;
    }

    .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .legend-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: var(--dsh-accent, #5d87ff);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}
.legend-toggle:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}
.legend-toggle:active {
  opacity: 1;
  transform: translateY(0);
}
    
  `]
})
export class ChartWidgetComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() config!: ChartConfig;
  @ViewChild('chartCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  constructor(private cdr: ChangeDetectorRef) {}

  allVisible = true;
  private chart?: Chart;
  private initialized = false;

  shellStyles: Record<string, string> = {};

  ngAfterViewInit(): void {
    this.initialized = true;
    if (this.config) this.buildChart();
  }
  
  textColor = getComputedStyle(document.documentElement)
  .getPropertyValue('--dsh-text') || '#b6b6b6';

  accentColor = getComputedStyle(document.documentElement)
  .getPropertyValue('--dsh-accent') || '#b6b6b6';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.shellStyles = {
        ...(this.config.width ? { width: this.config.width } : {}),
        ...(this.config.height ? { height: this.config.height } : {}),
      };
      if (this.initialized) this.buildChart();
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  // ── Build / rebuild ──────────────────────────────────────────
  buildChart(): void {
    this.chart?.destroy();
    const isStacked = this.config.type === 'stackedBar';

    const ctx = this.canvasRef.nativeElement.getContext('2d')!;
    const colors = [
      ...(this.config.colors?.length ? this.config.colors : []),
      ...DEFAULT_COLORS
    ].filter((c, i, arr) => arr.indexOf(c) === i);

    const resolveKey = (row: any, key: string): any => {
      if (key in row) return row[key];
      const lower = key.toLowerCase();
      const found = Object.keys(row).find(k => k.toLowerCase() === lower);
      return found ? row[found] : null;
    };

    const labels = this.config.data.map(r => {
      const val = resolveKey(r, this.config.labelKey);
      if (val == null || val === '') return 'null';
      if (typeof val === 'object') {
        return Array.isArray(val) ? val.join(', ') : JSON.stringify(val);
      }
      return String(val);
    });


    let datasets: ChartDataset[] = [];
    const type = this.resolveChartJsType();

    const isMultiSeries = Array.isArray(this.config.valueKey);

    if (isMultiSeries) {
      const keys = this.config.valueKey as string[];
      datasets = keys.map((k, i) => ({
        label:k,
        hidden: !this.allVisible,
        // data: this.config.data.map(r => resolveKey(r, k) ?? 0),
        data: this.config.data.map(r => resolveKey(r, k) ?? null),
        backgroundColor: this.applyAlpha(colors[i % colors.length], type),
        borderColor: colors[i % colors.length],
        borderWidth: 2,
        fill: type === 'line' ? false : undefined,
        tension: 0.4,
        pointRadius: type === 'line' ? 4 : 0,
        minBarLength: type === 'bar' ? (this.config.minBarLength ?? 8) : undefined,
      } as ChartDataset));
    } else {
      const key = this.config.valueKey as string;
      datasets = [{
        label: String(this.config.title ?? ''),
        hidden: !this.allVisible, 
        data: this.config.data.map(r => Number(resolveKey(r, key)) || 0),
        backgroundColor: type === 'pie' || type === 'doughnut'
          ? labels.map((_, i) => colors[i % colors.length])
          : this.applyAlpha(colors[0], type),
        borderColor: type === 'pie' || type === 'doughnut'
          ? '#fff'
          : colors[0],
        borderWidth: type === 'pie' || type === 'doughnut' ? 2 : 2,
        fill: type === 'line' ? false : undefined,
        tension: 0.4,
        pointRadius: type === 'line' ? 4 : 0,
        minBarLength: type === 'bar' ? (this.config.minBarLength ?? 8) : undefined,
      } as ChartDataset];
    }

    (datasets as any).__showValueLabels = !!this.config.showValueLabels;
    const cfg: ChartConfiguration = {
      type,
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: type === 'pie' || type === 'doughnut',
        plugins: {
          legend: {
            display: this.config.showLegend ?? true,
            position: type === 'pie' || type === 'doughnut' ? 'bottom' : 'top',
            labels: {
              font: { size: 12 },
              boxWidth: 12,
              padding: 16,
              color: this.textColor,
            },
          },
          // tooltip: { mode: 'index', intersect: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            filter: (item) => item.raw !== null,
          },
        },
        scales:
          type !== 'pie' && type !== 'doughnut'
            ? {
                x: {
                  stacked: isStacked,
                  ticks: {
                    color: this.textColor,
                  },
                  grid: { display: this.config.showGrid ?? false },
                  title: {
                    display: !!this.config.xLabel,
                    text: this.config.xLabel,
                    color: this.textColor,
                  },
                },
                y: {
                  stacked: isStacked,
                  ticks: {
                    color: this.textColor,
                  },
                  grid: {
                    display: this.config.showGrid ?? true,
                    color: this.accentColor,
                  },
                  title: {
                    display: !!this.config.yLabel,
                    text: this.config.yLabel,
                    color: this.textColor,
                  },
                  beginAtZero: true,
                },
              }
            : {},
      },
    };

    
    this.chart = new Chart(ctx, cfg);
    this.cdr.markForCheck();
  }

  private resolveChartJsType(): ChartType {
    switch (this.config.type) {
      case 'pie': return 'pie';
      case 'doughnut': return 'doughnut';
      case 'bar': return 'bar';
      case 'stackedBar': return 'bar'; 
      case 'groupedBar': return 'bar'; 
      case 'horizontalBar': return 'bar';
      case 'line':
      default: return 'line';
    }
  }

  private applyAlpha(hex: string, type: ChartType | string): string | string[] {
    if (type === 'bar') return hex + '99';        
    if (type === 'line') return hex + '33';
    return hex;
  }

get hasLegend(): boolean {
  return this.config?.showLegend ?? true;
}

toggleAll(): void {
  this.allVisible = !this.allVisible;
  this.buildChart();
}
}
